import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Header } from "./components/Header";
import { Carrusel } from "./components/carrusel";
import { Productos } from "./pages/sitio/Productos";
import { Footer } from "./components/Footer";
import Nosotros from "./pages/sitio/Nosotros";
import PoliticaPrivacidad from "./pages/sitio/PoliticaPrivacidad";
import ScrollToTop from "./ScrollToTop";
import AOS from "aos";
import "aos/dist/aos.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import EstadoCuenta from "./pages/cuenta/EstadoCuenta";
import Asesores from "./pages/sitio/Asesores";
import Catalogo from "./pages/catalogo/Catalogo";
import CatalogDetail from "./pages/catalogo/CatalogDetail";
import CatalogoMotos from "./pages/catalogo/CatalogoMotos";
import FloatingContact from "./components/FloatingContact";
import Asistente from "./components/Asistente";
import NotFound from "./pages/sitio/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";

// Páginas internas — se cargan solo cuando el usuario las visita
const Dashboard        = lazy(() => import('./pages/dashboard/Dashboard'));
const Inventario       = lazy(() => import('./pages/inventario/Inventario'));
const Menu             = lazy(() => import('./pages/cuenta/Menu'));
const UsuarioDatos     = lazy(() => import('./pages/cuenta/UsuarioDatos'));
const AdminEstadoCuenta = lazy(() => import('./pages/cuenta/AdminEstadoCuenta'));
const CatalogAdmin     = lazy(() => import('./pages/catalogo/CatalogAdmin'));
const CategoryOrderAdmin = lazy(() => import('./pages/catalogo/CategoryOrderAdmin'));
const SolicitudVenta   = lazy(() => import('./pages/solicitudes/SolicitudVenta'));
const MisSolicitudes   = lazy(() => import('./pages/solicitudes/MisSolicitudes'));
const DetalleSolicitud = lazy(() => import('./pages/solicitudes/DetalleSolicitud'));
const DashboardResumido = lazy(() => import('./pages/dashboard/DashboardResumido'));
const Rutas            = lazy(() => import('./pages/solicitudes/Rutas'));
const ClientesProximos = lazy(() => import('./pages/cobranza/ClientesProximos'));
const ClientesExOrden  = lazy(() => import('./pages/cobranza/ClientesExOrden'));
const Cosechas         = lazy(() => import('./pages/cobranza/Cosechas'));
const CarteraBucket    = lazy(() => import('./pages/cobranza/CarteraBucket'));
const CumplimientoDepositos = lazy(() => import('./pages/cobranza/CumplimientoDepositos'));
const AvanceCobranza   = lazy(() => import('./pages/cobranza/AvanceCobranza'));
const AprobacionCreditos = lazy(() => import('./pages/cobranza/AprobacionCreditos'));
const ComprasSugeridas = lazy(() => import('./pages/compras/ComprasSugeridas'));
const CotizadorAdmin   = lazy(() => import('./pages/cotizador/CotizadorAdmin'));
const ReportesFacturacion = lazy(() => import('./pages/cobranza/ReportesFacturacion'));
const MiFacturacion    = lazy(() => import('./pages/cobranza/miFacturacion'));
const ResumenSolicitudes = lazy(() => import('./pages/solicitudes/ResumenSolicitudes'));
const CobranzaGestion  = lazy(() => import('./pages/cobranza/CobranzaGestion'));
const AutorizacionCredito    = lazy(() => import('./pages/cobranza/AutorizacionCredito'));
const MisVisitas             = lazy(() => import('./pages/solicitudes/MisVisitas'));
const Cotizaciones           = lazy(() => import('./pages/cotizaciones/Cotizaciones'));
const CotizacionesVendedor   = lazy(() => import('./pages/cotizaciones/CotizacionesVendedor'));
const RequisicionesVendedor  = lazy(() => import('./pages/compras/RequisicionesVendedor'));
const RequisicionesCompras   = lazy(() => import('./pages/compras/RequisicionesCompras'));
const Recolecciones          = lazy(() => import('./pages/compras/Recolecciones'));
const SeguimientoRecolecciones = lazy(() => import('./pages/compras/SeguimientoRecolecciones'));
const Tabulador              = lazy(() => import('./pages/cotizador/Tabulador'));
const Repositorio            = lazy(() => import('./pages/cuenta/Repositorio'));
const SolicitudExhibicion    = lazy(() => import('./pages/exhibiciones/SolicitudExhibicion'));
const ExhibicionesAdmin      = lazy(() => import('./pages/exhibiciones/ExhibicionesAdmin'));
const AuditoriaInventario    = lazy(() => import('./pages/inventario/AuditoriaInventario'));
const CatalogoCompetencia    = lazy(() => import('./pages/catalogo/CatalogoCompetencia'));
const CatalogoInventario     = lazy(() => import('./pages/inventario/CatalogoInventario'));
const CatalogoInventarioCompras = lazy(() => import('./pages/inventario/CatalogoInventarioCompras'));
const AdminUsuarios          = lazy(() => import('./pages/cuenta/AdminUsuarios'));
const MisAutorizaciones      = lazy(() => import('./pages/solicitudes/MisAutorizaciones'));

// Roles con asistente (se amplía conforme se habilite en el backend) y páginas donde no se muestra
const ROLES_CON_ASISTENTE = ['vendedor', 'cobranza', 'compras'];
const RUTAS_SIN_ASISTENTE = [
  "/", "/nosotros", "/politica-privacidad", "/productos", "/asesores",
  "/catalogo", "/catalogo-motos", "/estado-cuenta", "/adminestadocuenta",
];

// Único lugar donde sale el botón de WhatsApp: sitio público y panel del cliente.
// Toda pantalla interna nueva queda sin él por defecto.
const RUTAS_CON_WHATSAPP = [
  "/", "/nosotros", "/politica-privacidad", "/productos", "/asesores",
  "/catalogo", "/catalogo-motos", "/usuario",
];

function leerSesion() {
  try {
    return {
      token: localStorage.getItem('token'),
      rol: localStorage.getItem('role'),
      userId: localStorage.getItem('userId'),
    };
  } catch {
    return { token: null, rol: null, userId: null };
  }
}

function AppContent() {
  const [sinConexion, setSinConexion] = useState(!navigator.onLine);

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  useEffect(() => {
    const marcarOffline = () => setSinConexion(true);
    const marcarOnline  = () => setSinConexion(false);
    window.addEventListener('offline', marcarOffline);
    window.addEventListener('online',  marcarOnline);
    return () => {
      window.removeEventListener('offline', marcarOffline);
      window.removeEventListener('online',  marcarOnline);
    };
  }, []);

  const location = useLocation();

  const mostrarFloating =
    RUTAS_CON_WHATSAPP.includes(location.pathname) ||
    location.pathname.startsWith("/catalogo/");

  // El formulario de solicitud tiene barra fija inferior; el detalle no
  const enFormularioSolicitud =
    location.pathname === "/solicitud-venta" ||
    (location.pathname.startsWith("/solicitud-venta/") && !location.pathname.endsWith("/detalle"));

  const sesion = leerSesion();
  const mostrarAsistente =
    Boolean(sesion.token) &&
    ROLES_CON_ASISTENTE.includes(sesion.rol) &&
    !RUTAS_SIN_ASISTENTE.includes(location.pathname) &&
    !location.pathname.startsWith("/catalogo/") &&
    !enFormularioSolicitud;

  return (
    <>
      {sinConexion && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
          background: '#c0392b', color: '#fff', textAlign: 'center',
          padding: '10px 16px', fontSize: '14px', fontWeight: 500,
          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
        }}>
          Sin conexión a internet — revisa tu red e intenta de nuevo
        </div>
      )}
      <Header />
      <ScrollToTop />
      <ErrorBoundary>
      <Suspense fallback={<div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: '1rem' }}>Cargando...</div>}>
        <Routes>
          <Route path="/"                              element={<Carrusel />} />
          <Route path="/nosotros"                      element={<Nosotros />} />
          <Route path="/politica-privacidad"           element={<PoliticaPrivacidad />} />
          <Route path="/productos"                     element={<Productos />} />
          <Route path="/estado-cuenta"                 element={<EstadoCuenta />} />
          <Route path="/asesores"                      element={<Asesores />} />
          <Route path="/catalogo"                      element={<Catalogo />} />
          <Route path="/catalogo/:id"                  element={<CatalogDetail />} />
          <Route path="/catalogo-motos"                element={<CatalogoMotos />} />
          <Route path="/dashboard"                     element={<Dashboard />} />
          <Route path="/inventario"                    element={<Inventario />} />
          <Route path="/menu"                          element={<Menu />} />
          <Route path="/mis-solicitudes"               element={<MisSolicitudes />} />
          <Route path="/solicitud-venta/:numero/detalle" element={<DetalleSolicitud />} />
          <Route path="/solicitud-venta"               element={<SolicitudVenta />} />
          <Route path="/solicitud-venta/:numero"       element={<SolicitudVenta />} />
          <Route path="/catalog-admin"                 element={<CatalogAdmin />} />
          <Route path="/category-order-admin"          element={<CategoryOrderAdmin />} />
          <Route path="/reporte"                       element={<DashboardResumido />} />
          <Route path="/rutas"                         element={<Rutas />} />
          <Route path="/usuario"                       element={<UsuarioDatos />} />
          <Route path="/adminestadocuenta"             element={<AdminEstadoCuenta />} />
          <Route path="/clientes-proximos"             element={<ClientesProximos />} />
          <Route path="/clientes-ex-orden"             element={<ClientesExOrden />} />
          <Route path="/cosechas"                      element={<Cosechas />} />
          <Route path="/cartera-bucket"                element={<CarteraBucket />} />
          <Route path="/cumplimiento-depositos"        element={<CumplimientoDepositos />} />
          <Route path="/avance-cobranza"               element={<AvanceCobranza />} />
          <Route path="/aprobacion-creditos"           element={<AprobacionCreditos />} />
          <Route path="/compras-sugeridas"             element={<ComprasSugeridas />} />
          <Route path="/cotizador-admin"               element={<CotizadorAdmin />} />
          <Route path="/reportes/facturacion"          element={<ReportesFacturacion />} />
          <Route path="/mi-facturacion"                element={<MiFacturacion />} />
          <Route path="/resumen-solicitudes"           element={<ResumenSolicitudes />} />
          <Route path="/cobranza-gestion"              element={<CobranzaGestion />} />
          <Route path="/autorizacion-credito"          element={<AutorizacionCredito />} />
          <Route path="/mis-visitas"                   element={<MisVisitas />} />
          <Route path="/cotizaciones"                  element={<Cotizaciones />} />
          <Route path="/cotizaciones-vendedor"         element={<CotizacionesVendedor />} />
          <Route path="/requisiciones-vendedor"        element={<RequisicionesVendedor />} />
          <Route path="/requisiciones-compras"         element={<RequisicionesCompras />} />
          <Route path="/recolecciones"                 element={<Recolecciones />} />
          <Route path="/seguimiento-recolecciones"     element={<SeguimientoRecolecciones />} />
          <Route path="/tabulador"                     element={<Tabulador />} />
          <Route path="/repositorio"                   element={<Repositorio />} />
          <Route path="/solicitud-exhibicion"          element={<SolicitudExhibicion />} />
          <Route path="/exhibiciones-admin"            element={<ExhibicionesAdmin />} />
          <Route path="/auditoria-inventario"          element={<AuditoriaInventario />} />
          <Route path="/catalogo-competencia"          element={<CatalogoCompetencia />} />
          <Route path="/catalogo-inventario"           element={<CatalogoInventario />} />
          <Route path="/catalogo-inventario-compras"   element={<CatalogoInventarioCompras />} />
          <Route path="/admin-usuarios"                element={<AdminUsuarios />} />
          <Route path="/mis-autorizaciones"            element={<MisAutorizaciones />} />
          <Route path="*"                              element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>

      {mostrarFloating && <FloatingContact />}

      {/* key: al cambiar de usuario o de rol el chat arranca limpio */}
      {mostrarAsistente && <Asistente key={`${sesion.rol}-${sesion.userId}`} rol={sesion.rol} />}

      <Footer />
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
